/**
 * Decide se o socket do STOMP sobe num **Web Worker dedicado** ou no **main thread** (R2CLOUD-1169).
 *
 * O defeito medido em DEV em 31/07/2026: ao carregar o portal, o PRIMEIRO
 * `new Worker(new URL('./stomp.worker', import.meta.url), {type:'module'})` criado por
 * `StompService.initWorker()` nunca inicia. O request do script é enviado (visto no CDP em
 * `Network.requestWillBeSent`, por volta dos 4,9 s do boot) e **nunca recebe resposta**: não falha,
 * não é cancelado, fica pendente. O watchdog do R2CLOUD-1165 dispara aos 10 s, dá `terminate()` e
 * cria outro worker — esse responde, em ~6,7 s — e a sessão STOMP só fica de pé aos 21-26 s.
 *
 * **A causa é o service worker (ngsw) controlando a página, e NÃO o carregamento do script.** É o
 * que as medições fecham:
 *
 * - A/B do registro do ngsw: **8 de 8** cargas com o primeiro worker mudo enquanto o service worker
 *   controlava a página; **0 de 6** com o registro do service worker bloqueado. Sem service worker o
 *   worker responde em **2,8-3,2 s**.
 * - Forçar o caminho do main thread (`new StompWorker().work(input$)`) com o service worker ATIVO:
 *   **0 de 3** cargas mudas, socket `wss://…/stomp/web` aberto em **728-767 ms** e sessão STOMP de pé
 *   em ~4,3 s.
 *
 * ⚠️ **Já foi testado e NÃO resolve — não "conserte" isto de volta:**
 *
 * - `?ngsw-bypass` na URL do worker, que é o escape OFICIAL do ngsw (faz o `onFetch` retornar sem
 *   chamar `respondWith`): **4 de 4** cargas continuaram mudas.
 * - Pré-carregar o script com `fetch` antes de criar o worker: o arquivo sai em **8-13 ms**,
 *   quentíssimo, e ainda assim **4 de 4** continuaram mudas.
 * - Um `fetch()` do mesmo script é rápido em QUALQUER instante do boot (13-463 ms), o que descarta a
 *   hipótese de rede ou servidor lentos.
 *
 * O que trava é a criação do **worker client** sob uma página controlada por service worker; por isso
 * nada que mexa no *request* do script adianta. Em PROD **sempre** há service worker (o build de
 * produção registra o ngsw), então lá o primeiro Web Worker nunca inicia — o custo do defeito é 100 %
 * das cargas, não uma fração.
 *
 * Regras puras, sem Angular e sem rxjs de propósito, para serem testadas sem TestBed — mesmo arranjo
 * de `stomp-worker-watchdog.ts` e `stomp-backoff.ts`. Testes em test/stomp-worker-strategy.test.ts.
 */

/** O que se observa do browser para decidir onde o socket do STOMP vai rodar. */
export interface StompWorkerEnvironment {
    /** O browser expõe a API de Web Worker dedicado (`typeof Worker !== 'undefined'`). */
    workerAvailable: boolean;

    /** Há um service worker **controlando esta página** (`navigator.serviceWorker.controller`). */
    serviceWorkerControlled: boolean;
}

/**
 * Usa o Web Worker dedicado? Só quando o browser tem `Worker` **e** nenhum service worker controla a
 * página.
 *
 * O sinal é `controller`, e não "existe um service worker registrado": uma página ainda não
 * reivindicada pelo ngsw (primeiríssima carga, ou hard-reload) se comporta como página sem service
 * worker nenhum — e foi assim que as 6 cargas do braço de controle subiram o worker em 2,8-3,2 s.
 *
 * Quando a resposta é `false`, o `StompService` cai no caminho que ele já tinha para browser sem Web
 * Worker: `new StompWorker().work(input$)`, rodando no main thread. Não é caminho novo nem código
 * morto ressuscitado — é o mesmo que já atendia esse cenário, agora medido como o **mais rápido** dos
 * dois com service worker ativo (socket em 728-767 ms contra 21-26 s).
 */
export function shouldUseDedicatedWorker(environment: StompWorkerEnvironment): boolean {
    return environment.workerAvailable && !environment.serviceWorkerControlled;
}

/**
 * Lê o ambiente do browser de verdade. Separada de `shouldUseDedicatedWorker` justamente para a
 * decisão ser testável sem browser.
 *
 * Vale reler a cada tentativa de conexão: o ngsw pode reivindicar a página DEPOIS do primeiro load
 * (`clients.claim()` na instalação inicial), e nesse caso a próxima conexão já enxerga o controller.
 */
export function readStompWorkerEnvironment(): StompWorkerEnvironment {
    return {
        workerAvailable: typeof Worker !== 'undefined',
        serviceWorkerControlled: isPageControlledByServiceWorker(),
    };
}

/**
 * Defensiva de propósito: `navigator.serviceWorker` não existe em contexto inseguro (http) e há
 * browser que **lança** ao acessá-lo (iframe com sandbox, janela privada). Um throw aqui rebentaria
 * dentro do `initWorker()` e o portal ficaria sem STOMP nenhum — muito pior que o defeito que este
 * módulo conserta.
 *
 * Não conseguir ler a API significa que ela não está disponível nesse contexto e, portanto, que
 * nenhum service worker controla esta página: `false` é a resposta correta, não um chute conservador.
 */
function isPageControlledByServiceWorker(): boolean {
    try {
        return typeof navigator !== 'undefined' && !!navigator.serviceWorker?.controller;
    } catch {
        return false;
    }
}
